import { useEffect, useState } from 'react';
import { useField } from 'formik';
import { Flex, Switch } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { Stars03 } from '@kula-ai/icons-core';

// Gradient switch styles
const gradientSwitchStyles = {
  '& .chakra-switch__track': {
    bg: 'gray.200',
  },
  '& .chakra-switch__track[data-checked]': {
    bg: 'linear-gradient(181.67deg, #A3A7FF -4.05%, #BA72DF 98.58%)',
  },
  '& .chakra-switch__thumb': {
    boxShadow: '0px 2px 4px rgba(65, 59, 116, 0.5)',
  },
};

const FillWithAiFieldUI = ({ isChecked, onToggle, page = undefined }) => (
  <Flex
    bg="#F9F5FF"
    borderRadius="8px"
    p="12px"
    mx={page === 'add_interview' ? '24px' : '0px'}
    id="fill_with_ai_enabled"
    mt="12px"
    mb="16px"
    justifyContent="space-between"
    alignItems="center"
  >
    <Flex flexDir="column" gap="6px" flex="1">
      <Flex alignItems="center" gap="6px">
        <Stars03
          size={16}
          color="#605AFB"
          className="kula-icon"
          style={{ flexShrink: 0 }}
        />
        <KText
          fontSize="14px"
          fontWeight="550"
          lineHeight="20px"
          sx={{
            background: 'linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Allow interviewers to complete scorecards with AI?
        </KText>
      </Flex>
      <KText fontSize="14px" fontWeight="450" lineHeight="20px" color="#6D659D">
        Helps draft scorecards faster, improving completion rates and
        consistency
      </KText>
    </Flex>
    <Switch
      size="md"
      sx={gradientSwitchStyles}
      cursor="pointer"
      isChecked={isChecked}
      onChange={onToggle}
      ml="12px"
      flexShrink={0}
    />
  </Flex>
);

const SchFillWithAiFieldWithFormik = ({ page, updateSharedData }) => {
  const [field, , helpers] = useField('fill_with_ai_enabled');
  const { setValue } = helpers;
  const { value } = field;
  const isFillWithAiEnabled = value ?? false;

  const onToggle = (event) => {
    const { checked } = event.target;
    setValue(checked);
    if (updateSharedData) {
      updateSharedData('fill_with_ai_enabled', checked);
    }
  };

  return (
    <FillWithAiFieldUI
      isChecked={isFillWithAiEnabled}
      onToggle={onToggle}
      page={page}
    />
  );
};

const SchFillWithAiFieldWithRef = ({
  schedulerFormRef,
  updateSharedData,
  interviewPlan,
  page,
}) => {
  const [isChecked, setChecked] = useState(
    () => interviewPlan?.fill_with_ai_enabled ?? false
  );

  useEffect(() => {
    const fromForm = schedulerFormRef?.current?.values?.fill_with_ai_enabled;
    const fromPlan = interviewPlan?.fill_with_ai_enabled;
    const next = fromForm ?? fromPlan ?? false;
    const id = setTimeout(() => setChecked(next), 0);
    return () => clearTimeout(id);
  }, [schedulerFormRef, interviewPlan?.fill_with_ai_enabled]);

  const onToggle = (event) => {
    const { checked } = event.target;
    setChecked(checked);
    schedulerFormRef?.current?.setFieldValue?.('fill_with_ai_enabled', checked);
    if (updateSharedData) {
      updateSharedData('fill_with_ai_enabled', checked);
    }
  };

  return (
    <FillWithAiFieldUI isChecked={isChecked} onToggle={onToggle} page={page} />
  );
};

export const SchFillWithAiField = ({
  page = undefined,
  updateSharedData,
  schedulerFormRef,
  interviewPlan,
}) => {
  if (schedulerFormRef) {
    return (
      <SchFillWithAiFieldWithRef
        schedulerFormRef={schedulerFormRef}
        updateSharedData={updateSharedData}
        interviewPlan={interviewPlan}
        page={page}
      />
    );
  }
  return (
    <SchFillWithAiFieldWithFormik
      page={page}
      updateSharedData={updateSharedData}
    />
  );
};

export default SchFillWithAiField;
